import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getDefaultRoute } from '../components/common/RoleBasedRoute';
import { useBrandedImages, useCompanyBranding } from '../hooks/useBranding';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    
    // Get branded content
    const { loginLogo } = useBrandedImages();
    const { title, tagline } = useCompanyBranding();

    const handleLogin = async () => {
        if (!username || !password) {
            setError('Please enter username and password');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await login(username, password);
            // Get role-based default route for the user
            const defaultRoute = getDefaultRoute(response.user?.role);
            navigate(defaultRoute);
        } catch (err) {
            console.error('Login error:', err);
            
            // Handle different types of errors
            let errorMessage = 'Login failed. Please try again.';
            
            if (err.status === 401) {
                errorMessage = 'Invalid username or password. Please check your credentials and try again.';
            } else if (err.status === 400) {
                errorMessage = err.message || 'Invalid login data. Please check your input.';
            } else if (err.status === 500) {
                errorMessage = 'Server error. Please try again later.';
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleLogin();
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br   to-indigo-900 flex items-center justify-center p-4 bg-drk-900">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center rounded-full mb-4">
                        {loginLogo && (
                            <img 
                                src={loginLogo} 
                                alt="Company Logo" 
                                
                            />
                        )}
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
                    <p className="text-gray-600 mt-2">{tagline}</p> 
                </div>

                <div className="space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-1x1 font-normal text-black mb-1">
                            Username
                        </label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => {
                                setUsername(e.target.value);
                                if (error) setError(''); // Clear error when user starts typing
                            }}
                            onKeyPress={handleKeyPress}
                            className=" outline-none w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter your username"
                        />
                    </div>

                    <div>
                        <label className="text-1x1 font-normal text-black mb-1">
                            Password
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (error) setError(''); // Clear error when user starts typing
                            }}
                            onKeyPress={handleKeyPress}
                            className="outline-none w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter your password"
                        />
                    </div>

                    <button
                        onClick={handleLogin}
                        disabled={loading}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Login;
